import React, { useState, useEffect, useMemo } from 'react';

const API = 'https://api.glowytics.com';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function gpmColor(gpm) {
  if (gpm === null || gpm === undefined) return 'text-gray-400';
  if (gpm < 20) return 'text-red-600';
  if (gpm < 35) return 'text-orange-500';
  if (gpm < 50) return 'text-green-600';
  return 'text-emerald-700';
}

function coverageColor(months) {
  if (months === null || months === undefined) return 'text-gray-400';
  if (months <= 2) return 'text-red-600';
  if (months <= 4) return 'text-orange-500';
  if (months <= 6) return 'text-green-600';
  if (months <= 8) return 'text-blue-600';
  return 'text-purple-600';
}

function coverageLabel(months) {
  if (months === null || months === undefined) return 'No sales';
  if (months <= 2) return 'Low';
  if (months <= 4) return 'Medium';
  if (months <= 6) return 'Healthy';
  if (months <= 8) return 'High';
  return 'Overstock';
}

function coverageBg(months) {
  if (months === null || months === undefined) return 'bg-gray-50';
  if (months <= 2) return 'bg-red-50';
  if (months <= 4) return 'bg-orange-50';
  if (months <= 6) return 'bg-green-50';
  if (months <= 8) return 'bg-blue-50';
  return 'bg-purple-50';
}

// Product list inside a brand/category row (Shopify): click a product to see its sizes.
function ProductBreakdown({ products, months }) {
  const [open, setOpen] = useState({});
  if (!products || products.length === 0) return null;
  return (
    <div className="bg-white rounded-lg border p-4 md:col-span-2">
      <div className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-3">Products ({products.length})</div>
      <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white">
            <tr className="text-gray-500 border-b">
              <th className="text-left py-2 pr-2 font-semibold">Product</th>
              <th className="text-left py-2 px-2 font-semibold">Status</th>
              <th className="text-right py-2 px-2 font-semibold">Stock</th>
              <th className="text-right py-2 px-2 font-semibold">{months}M Sales</th>
              <th className="text-right py-2 px-2 font-semibold">Avg / mo</th>
              <th className="text-right py-2 pl-2 font-semibold">Coverage</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => (
              <React.Fragment key={p.productId}>
                <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(o => ({ ...o, [p.productId]: !o[p.productId] }))}>
                  <td className="py-1.5 pr-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-gray-400 inline-block transition-transform ${open[p.productId] ? 'rotate-90' : ''}`}>▶</span>
                      {p.image ? <img src={p.image} alt="" className="w-8 h-8 object-cover rounded" /> : <div className="w-8 h-8 rounded bg-gray-100" />}
                      <span className="font-medium text-gray-900">{p.brand}</span>
                    </div>
                  </td>
                  <td className="py-1.5 px-2 text-gray-500">{STATUS_LABEL[p.status] || p.status}</td>
                  <td className="py-1.5 px-2 text-right font-bold text-gray-900">{fmt(p.stockQty)}</td>
                  <td className="py-1.5 px-2 text-right text-gray-600">{fmt(p.totalSold6m)}</td>
                  <td className="py-1.5 px-2 text-right text-gray-600">{fmt(p.avgMonthlySales)}</td>
                  <td className={`py-1.5 pl-2 text-right font-bold ${coverageColor(p.coverageMonths)}`}>{p.coverageMonths !== null ? `${p.coverageMonths} mo` : '—'}</td>
                </tr>
                {open[p.productId] && (
                  <tr className="bg-slate-50 border-b">
                    <td colSpan={6} className="py-2 pl-12 pr-2"><VariantTable variants={p.variants} months={months} /></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATUS_LABEL = { ACTIVE: 'Active', DRAFT: 'Draft', ARCHIVED: 'Archived', DELETED: 'Deleted' };

// Coverage buckets, same thresholds as coverageLabel; the distribution cards filter the table by these.
const BUCKETS = [
  { key: 'low', label: 'Low (≤2 mo)', test: (m) => m !== null && m <= 2, cls: 'bg-red-50 border-red-100', num: 'text-red-600', txt: 'text-red-500', ring: 'ring-red-400' },
  { key: 'medium', label: 'Medium (2-4 mo)', test: (m) => m !== null && m > 2 && m <= 4, cls: 'bg-orange-50 border-orange-100', num: 'text-orange-600', txt: 'text-orange-500', ring: 'ring-orange-400' },
  { key: 'healthy', label: 'Healthy (4-6 mo)', test: (m) => m !== null && m > 4 && m <= 6, cls: 'bg-green-50 border-green-100', num: 'text-green-600', txt: 'text-green-500', ring: 'ring-green-400' },
  { key: 'high', label: 'High (6-8 mo)', test: (m) => m !== null && m > 6 && m <= 8, cls: 'bg-blue-50 border-blue-100', num: 'text-blue-600', txt: 'text-blue-500', ring: 'ring-blue-400' },
  { key: 'overstock', label: 'Overstock (8+ mo)', test: (m) => m !== null && m > 8, cls: 'bg-purple-50 border-purple-100', num: 'text-purple-600', txt: 'text-purple-500', ring: 'ring-purple-400' },
  { key: 'noSales', label: 'No sales data', test: (m) => m === null, cls: 'bg-gray-50 border-gray-100', num: 'text-gray-500', txt: 'text-gray-400', ring: 'ring-gray-400' },
];

// Low-stock products ranked by the net revenue they bring per month: the ones to restock first.
// Built from product rows (product view, or the products nested in brand/category rows).
function RestockPriority({ products: all, months }) {
  const [showAll, setShowAll] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const products = activeOnly ? (all || []).filter(p => p.status === 'ACTIVE') : all;
  if (!all || all.length === 0) {
    return (
      <div className="bg-white border rounded-xl p-4 text-sm text-gray-500">
        <div className="font-bold text-gray-900 mb-1">Restock Priority</div>
        No sold, still-listed product is at 2 months of coverage or below.
      </div>
    );
  }
  const totalMonthly = products.reduce((s, p) => s + p.monthlyRevenue, 0);
  const rows = showAll ? products : products.slice(0, 20);
  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-1">
        <div>
          <div className="font-bold text-gray-900">Restock Priority</div>
          <div className="text-xs text-gray-500">
            {products.length} low-stock products (≤2 mo coverage), ranked by avg monthly net sales · together ₺{fmtMoney(totalMonthly)} / month
          </div>
        </div>
        <div className="flex flex-col md:items-end gap-1">
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
            Active products only
          </label>
          <div className="text-xs text-gray-400">Days left = coverage × 30 · Sizes out = sizes that sold in {months}M but have 0 stock</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wider">
              <th className="text-left px-4 py-2.5 font-semibold">#</th>
              <th className="text-left px-4 py-2.5 font-semibold">Product</th>
              <th className="text-right px-4 py-2.5 font-semibold">Net Sales / mo</th>
              <th className="text-right px-4 py-2.5 font-semibold">Avg Units / mo</th>
              <th className="text-right px-4 py-2.5 font-semibold">Stock</th>
              <th className="text-right px-4 py-2.5 font-semibold">Days Left</th>
              <th className="text-right px-4 py-2.5 font-semibold">Sizes Out</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.productId} className="border-b border-gray-100">
                <td className="px-4 py-2 text-gray-400 text-xs">{i + 1}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    {p.image ? <img src={p.image} alt="" className="w-9 h-9 object-cover rounded" /> : <div className="w-9 h-9 rounded bg-gray-100" />}
                    <div>
                      <div className="font-semibold text-gray-900">{p.brand}</div>
                      <div className="text-xs text-gray-400">{p.category}{p.status && p.status !== 'ACTIVE' ? ` · ${STATUS_LABEL[p.status] || p.status}` : ''}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2 text-right font-bold text-gray-900">₺{fmtMoney(p.monthlyRevenue)}</td>
                <td className="px-4 py-2 text-right text-gray-700">{fmt(p.avgMonthlySales)}</td>
                <td className="px-4 py-2 text-right text-gray-700">{fmt(p.stockQty)}</td>
                <td className="px-4 py-2 text-right">
                  {p.stockQty === 0
                    ? <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Out of stock</span>
                    : <span className="font-bold text-red-600">{p.daysLeft}</span>}
                </td>
                <td className={`px-4 py-2 text-right ${p.sizesOut > 0 ? 'font-semibold text-red-600' : 'text-gray-400'}`}>
                  {p.sizesTotal > 1 ? `${p.sizesOut} / ${p.sizesTotal}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {products.length > 20 && (
        <button onClick={() => setShowAll(v => !v)} className="w-full py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 border-t">
          {showAll ? 'Show top 20' : `Show all ${products.length}`}
        </button>
      )}
    </div>
  );
}

// Size / variant level: stock and sales matched by SKU.
function VariantTable({ variants, months }) {
  if (!variants || variants.length === 0) return <div className="text-xs text-gray-400">No variant data</div>;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-gray-500 border-b">
          <th className="text-left py-1.5 pr-2 font-semibold">Variant</th>
          <th className="text-left py-1.5 px-2 font-semibold">SKU</th>
          <th className="text-right py-1.5 px-2 font-semibold">Stock</th>
          <th className="text-right py-1.5 px-2 font-semibold">{months}M Sales</th>
          <th className="text-right py-1.5 pl-2 font-semibold">Coverage</th>
        </tr>
      </thead>
      <tbody>
        {variants.map(v => (
          <tr key={v.sku || v.variant} className="border-b border-gray-100">
            <td className="py-1 pr-2 text-gray-900">{v.variant}</td>
            <td className="py-1 px-2 text-gray-400">{v.sku || '—'}</td>
            <td className={`py-1 px-2 text-right font-bold ${v.stock === 0 && v.sold > 0 ? 'text-red-600' : 'text-gray-900'}`}>{fmt(v.stock)}</td>
            <td className="py-1 px-2 text-right text-gray-600">{fmt(v.sold)}</td>
            <td className={`py-1 pl-2 text-right font-semibold ${coverageColor(v.coverageMonths)}`}>{v.coverageMonths !== null ? `${v.coverageMonths} mo` : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function IkasStockCoverage({ source = 'ikas' }) {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('stockQty');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [groupBy, setGroupBy] = useState('brand');
  const [bucket, setBucket] = useState(null);
  const groupLabel = groupBy === 'category' ? 'Category' : groupBy === 'product' ? 'Product' : 'Brand';
  const isShopify = source === 'shopify';
  // Shopify stores without unit costs → no GPM columns (ikas responses have no hasCost flag).
  const showGpm = data?.hasCost !== false;
  const colCount = showGpm ? 11 : 8;

  useEffect(() => {
    if (!brandId) return;
    setLoading(true);
    setExpanded({});
    setBucket(null);
    fetch(`${API}/api/${source}/stock/coverage?months=6${groupBy !== 'brand' ? `&by=${groupBy}` : ''}`, {
      headers: { 'X-Brand-ID': brandId },
      credentials: 'include'
    })
      .then(r => r.json())
      .then(d => {
        if (d && d.error && !d.brands) { setData(null); setError(d.error); return; }
        setData(d); setError(null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [brandId, groupBy, source]);

  const sorted = useMemo(() => {
    if (!data?.brands) return [];
    let arr = data.brands.map(b => ({ ...b, grossProfit: (b.revenue || 0) - (b.cogs || 0) }));
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(b => b.brand.toLowerCase().includes(q));
    }
    const activeBucket = BUCKETS.find(x => x.key === bucket);
    if (activeBucket) arr = arr.filter(b => activeBucket.test(b.coverageMonths));
    arr.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (va === null || va === undefined) va = sortDir === 'asc' ? 99999 : -99999;
      if (vb === null || vb === undefined) vb = sortDir === 'asc' ? 99999 : -99999;
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [data, sortKey, sortDir, search, bucket]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Distribution summary
  const distribution = useMemo(() => {
    if (!data?.brands) return null;
    const out = {};
    for (const x of BUCKETS) out[x.key] = data.brands.filter(b => x.test(b.coverageMonths)).length;
    return out;
  }, [data]);

  // Restock priority (Shopify): product rows with ≤2 mo coverage, ranked by monthly net sales.
  // Deleted products can't be restocked, so they're left out.
  const restock = useMemo(() => {
    if (!isShopify || !data?.brands) return null;
    const months = data.params?.months || 6;
    const products = groupBy === 'product' ? data.brands : data.brands.flatMap(b => b.products || []);
    return products
      .filter(p => p.status !== 'DELETED' && p.totalSold6m > 0 && p.coverageMonths !== null && p.coverageMonths <= 2)
      .map(p => {
        const sizes = (p.variants || []).filter(v => v.sku);
        return {
          ...p,
          monthlyRevenue: (p.revenue || 0) / months,
          daysLeft: Math.round(p.coverageMonths * 30),
          sizesTotal: sizes.length,
          sizesOut: sizes.filter(v => v.stock === 0 && v.sold > 0).length,
        };
      })
      .sort((a, b) => b.monthlyRevenue - a.monthlyRevenue);
  }, [data, groupBy, isShopify]);

  if (!brandId) return <div className="p-6 text-gray-500">Please select a brand first.</div>;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Stock Coverage Analysis</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {showGpm ? 'Stock coverage & gross profit margin analysis' : 'Stock coverage analysis'} by {groupLabel.toLowerCase()}
          </p>
          {data?.snapshotDate && (
            <p className="text-xs text-gray-400 mt-1 font-medium">Stock as of {new Date(data.snapshotDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
          )}
          {data?.params?.dateRange && (
            <p className="text-xs text-gray-400 mt-1 font-medium">
              Sales period: {new Date(data.params.dateRange.from + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} — {new Date(data.params.dateRange.to + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ({data.params.dateRange.days} days)
            </p>
          )}
        </div>
        <div className="inline-flex bg-gray-100 rounded-lg p-1 text-sm self-start">
          {[['brand', 'By Brand'], ['category', 'By Category'], ...(isShopify ? [['product', 'By Product']] : [])].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setGroupBy(k)}
              className={`px-3 py-1.5 rounded-md font-medium ${groupBy === k ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      )}

      {data && !loading && (
        <>
          {/* Methodology */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-600 space-y-1.5">
            <div className="font-bold text-gray-800 text-sm mb-2">Calculation Methodology</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
              <div><span className="font-semibold text-gray-700">Stock Coverage</span> = Current Stock Qty / Avg Monthly Sales (qty)</div>
              <div><span className="font-semibold text-gray-700">Avg Monthly Sales</span> = Last 180 days total sales qty / 6</div>
              <div><span className="font-semibold text-gray-700">Net Sales</span> = T-180 Day Net Revenue (final price × qty) / 1.10 (excl. 10% VAT)</div>
              {showGpm && <>
                <div><span className="font-semibold text-gray-700">Gross Profit</span> = Net Sales (excl. VAT) − COGS (buy price × qty sold)</div>
                <div><span className="font-semibold text-gray-700">GPM %</span> = Gross Profit / Net Sales (excl. VAT) × 100</div>
                <div className="text-gray-400 italic">Final price = discounted/net price · Buy price from latest stock snapshot</div>
              </>}
              {isShopify && (
                <div className="text-gray-400 italic md:col-span-2">
                  Brand = Shopify vendor · Category = Shopify product type · Stock includes active, draft and archived products · Size-level sales matched by SKU
                  {!showGpm && ' · No unit costs in Shopify, so gross profit / GPM are not shown'}
                </div>
              )}
              {!isShopify && groupBy === 'category' && (
                <div className="text-gray-400 italic md:col-span-2">Category = product's first ikas category, brand-name categories excluded (each product counted once)</div>
              )}
            </div>
          </div>

          {/* KPI Cards */}
          <div className={`grid grid-cols-2 ${showGpm ? 'md:grid-cols-6' : 'md:grid-cols-5'} gap-3`}>
            <div className="bg-white border rounded-xl p-4 text-center">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Overall Coverage</div>
              <div className={`text-2xl font-extrabold mt-1 ${coverageColor(data.totals.coverageMonths)}`}>
                {data.totals.coverageMonths !== null ? `${data.totals.coverageMonths} mo` : '—'}
              </div>
              <div className="text-xs text-gray-400 mt-1">{coverageLabel(data.totals.coverageMonths)}</div>
            </div>
            {showGpm && <div className="bg-white border rounded-xl p-4 text-center">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Gross Profit Margin</div>
              <div className={`text-2xl font-extrabold mt-1 ${gpmColor(data.totals.gpm)}`}>
                {data.totals.gpm !== null && data.totals.gpm !== undefined ? `${data.totals.gpm}%` : '—'}
              </div>
              <div className="text-xs text-gray-400 mt-1">excl. VAT · last 180 days</div>
            </div>}
            <div className="bg-white border rounded-xl p-4 text-center">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Total Stock</div>
              <div className="text-2xl font-extrabold mt-1 text-gray-900">{fmt(data.totals.totalStock)}</div>
              <div className="text-xs text-gray-400 mt-1">units</div>
            </div>
            <div className="bg-white border rounded-xl p-4 text-center">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Avg Monthly Sales</div>
              <div className="text-2xl font-extrabold mt-1 text-gray-900">{fmt(data.totals.avgMonthlySales)}</div>
              <div className="text-xs text-gray-400 mt-1">units / month</div>
            </div>
            <div className="bg-white border rounded-xl p-4 text-center">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">6-Month Sales</div>
              <div className="text-2xl font-extrabold mt-1 text-gray-900">{fmt(data.totals.totalSold6m)}</div>
              <div className="text-xs text-gray-400 mt-1">{data.params.activeMonths} active months</div>
            </div>
            <div className="bg-white border rounded-xl p-4 text-center">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{groupBy === 'category' ? 'Categories' : groupBy === 'product' ? 'Products' : 'Brands'}</div>
              <div className="text-2xl font-extrabold mt-1 text-gray-900">{data.totals.brandCount}</div>
              <div className="text-xs text-gray-400 mt-1">with stock</div>
            </div>
          </div>

          {/* Distribution (click a card to filter the table) */}
          {distribution && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
              {BUCKETS.map(x => (
                <button
                  key={x.key}
                  onClick={() => setBucket(cur => cur === x.key ? null : x.key)}
                  className={`${x.cls} border rounded-lg p-2 transition hover:shadow-sm ${bucket === x.key ? `ring-2 ${x.ring}` : ''} ${bucket && bucket !== x.key ? 'opacity-50' : ''}`}
                >
                  <div className={`font-bold text-lg ${x.num}`}>{distribution[x.key]}</div>
                  <div className={x.txt}>{x.label}</div>
                </button>
              ))}
            </div>
          )}

          {restock && <RestockPriority products={restock} months={data.params.months} />}

          {/* Search */}
          <div>
            <input
              type="text"
              className="border rounded-lg px-3 py-2 text-sm w-full md:w-72"
                            placeholder={`Search ${groupLabel.toLowerCase()}...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {bucket && (
              <button onClick={() => setBucket(null)} className="ml-2 text-xs text-gray-600 border rounded-full px-3 py-1 hover:bg-gray-50">
                Showing {BUCKETS.find(x => x.key === bucket)?.label} · {sorted.length} — clear ✕
              </button>
            )}
          </div>

          {/* Table */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wider">
                    <th className="text-left px-4 py-3 font-semibold">#</th>
                    <th className="text-left px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('brand')}>
                      {groupLabel} <SortIcon col="brand" />
                    </th>
                    <th className="text-right px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('stockQty')}>
                      Stock Qty <SortIcon col="stockQty" />
                    </th>
                    <th className="text-right px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('avgMonthlySales')}>
                      Avg Monthly Sales <SortIcon col="avgMonthlySales" />
                    </th>
                    <th className="text-right px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('totalSold6m')}>
                      6M Total Sales <SortIcon col="totalSold6m" />
                    </th>
                    <th className="text-right px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('revenue')}>
                      Net Sales <SortIcon col="revenue" />
                    </th>
                    {showGpm && <>
                      <th className="text-right px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('grossProfit')}>
                        Gross Profit <SortIcon col="grossProfit" />
                      </th>
                      <th className="text-right px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('gpm')}>
                        GPM % <SortIcon col="gpm" />
                      </th>
                    </>}
                    <th className="text-right px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('coverageMonths')}>
                      Coverage <SortIcon col="coverageMonths" />
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-left px-4 py-3 font-semibold">Visual</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((b, i) => {
                    const maxCoverage = 24;
                    const pct = b.coverageMonths !== null ? Math.min(b.coverageMonths / maxCoverage * 100, 100) : 0;
                    const isOpen = !!expanded[b.brand];
                    const gp = (b.revenue || 0) - (b.cogs || 0);
                    return (
                      <React.Fragment key={b.brand}>
                        <tr
                          className={`border-b hover:bg-gray-50 cursor-pointer ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} ${isOpen ? 'bg-blue-50/40' : ''}`}
                          onClick={() => setExpanded(prev => ({ ...prev, [b.brand]: !prev[b.brand] }))}
                        >
                          <td className="px-4 py-2.5 text-gray-400 text-xs">
                            <span className={`inline-block transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                          </td>
                          <td className="px-4 py-2.5 font-semibold text-gray-900">
                            {groupBy === 'product' ? (
                              <div className="flex items-center gap-2">
                                {b.image ? <img src={b.image} alt="" className="w-9 h-9 object-cover rounded" /> : <div className="w-9 h-9 rounded bg-gray-100" />}
                                <div>
                                  <div>{b.brand}</div>
                                  <div className="text-xs font-normal text-gray-400">{b.category}{b.status && b.status !== 'ACTIVE' ? ` · ${STATUS_LABEL[b.status] || b.status}` : ''}</div>
                                </div>
                              </div>
                            ) : b.brand}
                          </td>
                          <td className="px-4 py-2.5 text-right font-bold text-gray-900">{fmt(b.stockQty)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-700">{fmt(b.avgMonthlySales)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-500">{fmt(b.totalSold6m)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-700">{b.revenue ? `₺${fmtMoney(b.revenue)}` : '—'}</td>
                          {showGpm && <>
                            <td className={`px-4 py-2.5 text-right font-semibold ${gp > 0 ? 'text-green-700' : 'text-red-600'}`}>
                              {b.revenue ? `₺${fmtMoney(gp)}` : '—'}
                            </td>
                            <td className={`px-4 py-2.5 text-right font-bold ${gpmColor(b.gpm)}`}>
                              {b.gpm !== null && b.gpm !== undefined ? `${b.gpm}%` : '—'}
                            </td>
                          </>}
                          <td className={`px-4 py-2.5 text-right font-extrabold text-lg ${coverageColor(b.coverageMonths)}`}>
                            {b.coverageMonths !== null ? `${b.coverageMonths}` : '—'}
                            <span className="text-xs font-normal ml-0.5">mo</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${coverageBg(b.coverageMonths)} ${coverageColor(b.coverageMonths)}`}>
                              {coverageLabel(b.coverageMonths)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 w-32">
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  b.coverageMonths === null ? 'bg-gray-200' :
                                  b.coverageMonths <= 2 ? 'bg-red-400' :
                                  b.coverageMonths <= 4 ? 'bg-orange-400' :
                                  b.coverageMonths <= 6 ? 'bg-green-400' :
                                  b.coverageMonths <= 8 ? 'bg-blue-400' : 'bg-purple-400'
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50 border-b">
                            <td colSpan={colCount} className="px-6 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Stock Coverage Calculation */}
                                <div className="bg-white rounded-lg border p-4">
                                  <div className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                                    <span className="w-5 h-5 rounded bg-gray-900 text-white flex items-center justify-center text-[10px]">1</span>
                                    Stock Coverage — {b.brand}
                                  </div>
                                  <div className="space-y-2 text-xs">
                                    <div className="flex justify-between py-1.5 border-b border-dashed">
                                      <span className="text-gray-500">Current Stock</span>
                                      <span className="font-bold text-gray-900">{fmt(b.stockQty)} units</span>
                                    </div>
                                    <div className="flex justify-between py-1.5 border-b border-dashed">
                                      <span className="text-gray-500">Total Sales (180 days)</span>
                                      <span className="font-bold text-gray-900">{fmt(b.totalSold6m)} units</span>
                                    </div>
                                    <div className="flex justify-between py-1.5 border-b border-dashed">
                                      <span className="text-gray-500">Avg Monthly Sales</span>
                                      <span className="text-gray-700">{fmt(b.totalSold6m)} / 6 = <span className="font-bold">{fmt(b.avgMonthlySales)} units/mo</span></span>
                                    </div>
                                    <div className="flex justify-between py-2 bg-gray-50 rounded px-2 -mx-2">
                                      <span className="font-semibold text-gray-700">Stock Coverage</span>
                                      <span className="text-gray-700">
                                        {b.avgMonthlySales > 0
                                          ? <>{fmt(b.stockQty)} / {fmt(b.avgMonthlySales)} = <span className={`font-extrabold text-sm ${coverageColor(b.coverageMonths)}`}>{b.coverageMonths} months</span></>
                                          : <span className="text-gray-400">No sales data</span>
                                        }
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Profitability Calculation */}
                                {showGpm && <div className="bg-white rounded-lg border p-4">
                                  <div className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                                    <span className="w-5 h-5 rounded bg-gray-900 text-white flex items-center justify-center text-[10px]">2</span>
                                    Gross Profit Margin — {b.brand}
                                  </div>
                                  <div className="space-y-2 text-xs">
                                    <div className="flex justify-between py-1.5 border-b border-dashed">
                                      <span className="text-gray-500">Gross Revenue (KDV dahil)</span>
                                      <span className="font-bold text-gray-900">₺{fmtMoney(b.revenueGross || 0)}</span>
                                    </div>
                                    <div className="flex justify-between py-1.5 border-b border-dashed">
                                      <span className="text-gray-500">VAT Deduction (÷ 1.10)</span>
                                      <span className="text-gray-700">₺{fmtMoney(b.revenueGross || 0)} / 1.10 = <span className="font-bold">₺{fmtMoney(b.revenue || 0)}</span></span>
                                    </div>
                                    <div className="flex justify-between py-1.5 border-b border-dashed">
                                      <span className="text-gray-500">Net Sales (KDV hariç)</span>
                                      <span className="font-bold text-gray-900">₺{fmtMoney(b.revenue || 0)}</span>
                                    </div>
                                    <div className="flex justify-between py-1.5 border-b border-dashed">
                                      <span className="text-gray-500">COGS (buy price × qty)</span>
                                      <span className="font-bold text-gray-900">₺{fmtMoney(b.cogs || 0)}</span>
                                    </div>
                                    <div className="flex justify-between py-1.5 border-b border-dashed">
                                      <span className="text-gray-500">Gross Profit</span>
                                      <span className={`font-bold ${gp > 0 ? 'text-green-700' : 'text-red-600'}`}>
                                        ₺{fmtMoney(b.revenue || 0)} − ₺{fmtMoney(b.cogs || 0)} = ₺{fmtMoney(gp)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between py-2 bg-gray-50 rounded px-2 -mx-2">
                                      <span className="font-semibold text-gray-700">GPM %</span>
                                      <span className="text-gray-700">
                                        {b.revenue > 0
                                          ? <>₺{fmtMoney(gp)} / ₺{fmtMoney(b.revenue)} × 100 = <span className={`font-extrabold text-sm ${gpmColor(b.gpm)}`}>{b.gpm}%</span></>
                                          : <span className="text-gray-400">No revenue</span>
                                        }
                                      </span>
                                    </div>
                                  </div>
                                </div>}

                                {/* Size breakdown (product view) */}
                                {groupBy === 'product' && (
                                  <div className="bg-white rounded-lg border p-4">
                                    <div className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-3">Sizes / Variants — {b.brand}</div>
                                    <VariantTable variants={b.variants} months={data.params.months} />
                                  </div>
                                )}

                                {/* Monthly Sales Breakdown */}
                                {b.monthlySales && b.monthlySales.length > 0 && (
                                  <div className="bg-white rounded-lg border p-4 md:col-span-2">
                                    <div className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                                      <span className="w-5 h-5 rounded bg-gray-900 text-white flex items-center justify-center text-[10px]">3</span>
                                      Monthly Sales Breakdown — {b.brand}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {b.monthlySales.map(m => (
                                        <div key={m.month} className="bg-gray-50 border rounded-lg px-3 py-2 text-center min-w-[90px]">
                                          <div className="text-[10px] text-gray-400 font-medium">
                                            {new Date(m.month + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                          </div>
                                          <div className="text-sm font-bold text-gray-900 mt-0.5">{fmt(m.qty)}</div>
                                          <div className="text-[10px] text-gray-400">units</div>
                                        </div>
                                      ))}
                                      <div className="bg-gray-900 text-white rounded-lg px-3 py-2 text-center min-w-[90px]">
                                        <div className="text-[10px] font-medium opacity-70">TOTAL</div>
                                        <div className="text-sm font-bold mt-0.5">{fmt(b.totalSold6m)}</div>
                                        <div className="text-[10px] opacity-70">÷ 6 = {fmt(b.avgMonthlySales)}/mo</div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {b.products && <ProductBreakdown products={b.products} months={data.params.months} />}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
